import { motion } from "framer-motion";
import { ScanLine, Disc3, Droplets, Wrench, Cog, Plus, ArrowUpRight } from "lucide-react";
import { SERVICES } from "../../lib/data";
import { Reveal, Eyebrow } from "./Reveal";

const ICONS = { diag: ScanLine, brakes: Disc3, oil: Droplets, maint: Wrench, swap: Cog, more: Plus };
const SPANS = ["md:col-span-7 md:row-span-2", "md:col-span-5", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4"];

const Card = ({ s, i, onBook }) => {
  const Icon = ICONS[s.key];
  const feature = i === 0;
  return (
    <motion.button
      type="button"
      onClick={() => onBook(s.name)}
      data-testid={`service-card-${s.key}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -6 }}
      className={`group relative flex min-h-[240px] flex-col justify-between overflow-hidden rounded-3xl border border-white/10 bg-[var(--surface)] p-7 text-left transition-colors duration-300 hover:border-[var(--mag)]/60 sm:p-9 ${SPANS[i]}`}
    >
      {feature && (
        <>
          <img src="/img/mech1.webp" alt="Engine bay inspection" className="absolute inset-0 h-full w-full object-cover opacity-50 grayscale transition-[transform,opacity] duration-700 group-hover:scale-105 group-hover:opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07070b] via-[#07070b]/70 to-[#07070b]/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_20%,rgba(255,43,214,0.35),transparent_55%)]" />
        </>
      )}
      <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-[var(--mag)]/0 blur-3xl transition-colors duration-500 group-hover:bg-[var(--mag)]/25" />
      <div className="relative flex items-start justify-between">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-black/40 backdrop-blur">
          <Icon className="h-6 w-6 text-[var(--mag)] transition-transform duration-500 group-hover:rotate-[20deg]" />
        </span>
        <span className="font-mono-x text-sm text-zinc-400">0{i + 1}</span>
      </div>
      <div className={`relative ${feature ? "mt-40 md:mt-0" : "mt-10"}`}>
        <h3 className={`font-race text-white ${feature ? "text-5xl sm:text-7xl" : "text-4xl"}`}>{s.name}</h3>
        <p className={`mt-3 text-zinc-300 ${feature ? "max-w-md text-base" : "text-sm"}`}>{s.desc}</p>
        <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-5">
          <span className="font-mono-x text-sm font-bold text-[var(--pink-text)]">{s.price}</span>
          <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-white">
            Book <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </motion.button>
  );
};

export const Services = ({ onBook }) => (
  <section id="services" data-testid="services-section" className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 lg:px-12 lg:py-40">
    <Reveal className="mb-16 grid gap-8 lg:grid-cols-12 lg:items-end">
      <div className="lg:col-span-7">
        <Eyebrow num="01">Services</Eyebrow>
        <h2 className="font-race mt-6 text-6xl leading-[0.9] text-white sm:text-7xl lg:text-8xl">
          Full shop. <span className="text-neon">Zero</span> tow truck.
        </h2>
      </div>
      <p className="text-base text-zinc-300 md:text-lg lg:col-span-5">
        Everything rolls up to your home, work or wherever the car stopped. Tap a service to start your booking.
      </p>
    </Reveal>
    <div className="grid grid-cols-1 gap-5 md:auto-rows-[minmax(260px,auto)] md:grid-cols-12">
      {SERVICES.map((s, i) => <Card key={s.key} s={s} i={i} onBook={onBook} />)}
    </div>
  </section>
);
